import Phaser from 'phaser'

import type { CrowdCanvasState, CrowdCanvasStroke } from '@shared/games/protocol'
import { isCrowdCanvasState } from '@shared/games/protocol'

const WORLD_W = 960
const WORLD_H = 540

function deviceDpr(): number {
  if (typeof window === 'undefined') return 2
  return Math.min(Math.max(window.devicePixelRatio || 1, 1), 2)
}

function paintScale(): number {
  return Math.max(3, Math.ceil(deviceDpr() * 2))
}

function strokeToContext(
  ctx: CanvasRenderingContext2D,
  points: { x: number; y: number }[],
  scale: number,
  color: string,
  width: number,
) {
  if (points.length < 2) return
  const pts = points.map((p) => ({ x: p.x * scale, y: p.y * scale }))
  const lw = Math.max(1.25, width * scale)

  ctx.save()
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.strokeStyle = color
  ctx.fillStyle = color
  ctx.lineWidth = lw
  ctx.imageSmoothingEnabled = true
  ;(
    ctx as CanvasRenderingContext2D & { imageSmoothingQuality?: string }
  ).imageSmoothingQuality = 'high'

  ctx.beginPath()
  ctx.arc(pts[0].x, pts[0].y, lw / 2, 0, Math.PI * 2)
  ctx.fill()

  ctx.beginPath()
  ctx.moveTo(pts[0].x, pts[0].y)
  if (pts.length === 2) {
    ctx.lineTo(pts[1].x, pts[1].y)
  } else {
    for (let i = 1; i < pts.length - 1; i += 1) {
      const midX = (pts[i].x + pts[i + 1].x) / 2
      const midY = (pts[i].y + pts[i + 1].y) / 2
      ctx.quadraticCurveTo(pts[i].x, pts[i].y, midX, midY)
    }
    const prev = pts[pts.length - 2]
    const last = pts[pts.length - 1]
    ctx.quadraticCurveTo(prev.x, prev.y, last.x, last.y)
  }
  ctx.stroke()

  ctx.beginPath()
  ctx.arc(pts[pts.length - 1].x, pts[pts.length - 1].y, lw / 2, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
}

class CrowdCanvasSpectatorScene extends Phaser.Scene {
  private boardTex: Phaser.Textures.CanvasTexture | null = null
  private boardImage: Phaser.GameObjects.Image | null = null
  private hudText: Phaser.GameObjects.Text | null = null
  private banner: Phaser.GameObjects.Text | null = null
  private drawnStrokeIds = new Set<string>()
  private drawnStrokePoints = new Map<string, number>()
  private lastHudKey = ''
  private lastBannerLeft = -1
  private lastState: CrowdCanvasState | null = null
  private labels = new Map<string, Phaser.GameObjects.Text>()

  constructor() {
    super('CrowdCanvasSpectator')
  }

  create() {
    const w = WORLD_W
    const h = WORLD_H
    this.applyHiDpiCamera(w, h)
    this.add.rectangle(w / 2, h / 2, w, h, 0xffffff)
    this.ensureBoard(w, h)

    this.hudText = this.add
      .text(16, 12, '', {
        fontSize: '14px',
        color: '#ffffff',
        fontFamily: 'Segoe UI, sans-serif',
        backgroundColor: '#6d28d9cc',
        padding: { x: 10, y: 6 },
      })
      .setDepth(100)

    this.banner = this.add
      .text(w / 2, h / 2, '', {
        fontSize: '72px',
        color: '#7c3aed',
        fontFamily: 'Segoe UI, sans-serif',
        align: 'center',
        stroke: '#ede9fe',
        strokeThickness: 8,
      })
      .setOrigin(0.5)
      .setDepth(200)
      .setVisible(false)
  }

  private applyHiDpiCamera(w: number, h: number) {
    const dpr = deviceDpr()
    this.scale.resize(Math.round(w * dpr), Math.round(h * dpr))
    this.cameras.main.setZoom(dpr)
    this.cameras.main.centerOn(w / 2, h / 2)
  }

  update() {
    // update() вызывается движком Phaser внутри своего RAF-цикла — React
    // ErrorBoundary и обёртка в GamesLayer такую ошибку не ловят. Без try/catch
    // падение здесь остановит весь game loop навсегда.
    try {
      const state = this.lastState
      if (!state || !this.banner) return
      if (state.phase === 'countdown' && state.countdownEndsAt) {
        const left = Math.max(
          1,
          Math.ceil((state.countdownEndsAt - Date.now()) / 1000),
        )
        if (left !== this.lastBannerLeft) {
          this.lastBannerLeft = left
          this.banner
            .setText(String(left))
            .setColor('#7c3aed')
            .setStroke('#ede9fe', 8)
            .setFontSize('72px')
            .setAlpha(0)
            .setVisible(true)
            .setScale(1.12)
          this.tweens.add({
            targets: this.banner,
            alpha: 1,
            scale: 1,
            duration: 220,
            ease: 'Sine.easeOut',
          })
        }
        return
      }
      if (this.lastBannerLeft !== -1 && state.phase !== 'countdown') {
        this.lastBannerLeft = -1
        this.banner.setVisible(false)
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[overlay/crowd-canvas] update() failed, skipping frame:', error)
    }
  }

  clearBoard() {
    this.clearPaint()
    this.hudText?.setText('')
    this.banner?.setVisible(false)
    for (const label of this.labels.values()) label.destroy()
    this.labels.clear()
    this.lastHudKey = ''
    this.lastBannerLeft = -1
    this.lastState = null
  }

  applyState(state: CrowdCanvasState) {
    this.lastState = state
    this.applyHiDpiCamera(state.arena.width, state.arena.height)
    this.ensureBoard(state.arena.width, state.arena.height)
    this.banner?.setPosition(state.arena.width / 2, state.arena.height / 2)
    this.syncStrokes(state)
    this.syncLabels(state)
    this.syncHud(state)
  }

  private ensureBoard(w: number, h: number) {
    const s = paintScale()
    const tw = Math.round(w * s)
    const th = Math.round(h * s)
    const key = 'crowdBoardSpectator'

    if (
      this.boardTex &&
      (this.boardTex.width !== tw || this.boardTex.height !== th)
    ) {
      this.boardImage?.destroy()
      this.textures.remove(key)
      this.boardTex = null
      this.boardImage = null
      this.drawnStrokeIds.clear()
      this.drawnStrokePoints.clear()
    }

    if (!this.boardTex) {
      const created = this.textures.createCanvas(key, tw, th)
      if (!created) return
      this.boardTex = created
      const ctx = created.getContext()
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, tw, th)
      created.refresh()
      this.textures.get(key).setFilter(Phaser.Textures.FilterMode.LINEAR)
      this.boardImage = this.add
        .image(0, 0, key)
        .setOrigin(0, 0)
        .setDisplaySize(w, h)
        .setDepth(1)
    } else {
      this.boardImage?.setDisplaySize(w, h)
    }
  }

  private clearPaint() {
    if (!this.boardTex) return
    const ctx = this.boardTex.getContext()
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, this.boardTex.width, this.boardTex.height)
    this.boardTex.refresh()
    this.drawnStrokeIds.clear()
    this.drawnStrokePoints.clear()
  }

  private syncStrokes(state: CrowdCanvasState) {
    const ids = new Set(state.strokes.map((s) => s.id))
    if (
      state.phase === 'countdown' ||
      state.phase === 'lobby' ||
      (this.drawnStrokeIds.size > 0 &&
        [...this.drawnStrokeIds].some((id) => !ids.has(id)))
    ) {
      this.clearPaint()
    }
    for (const stroke of state.strokes) {
      const drawn = this.drawnStrokePoints.get(stroke.id) ?? 0
      if (stroke.points.length <= drawn) {
        this.drawnStrokeIds.add(stroke.id)
        continue
      }
      const from = Math.max(0, drawn - 1)
      const segmentPoints = stroke.points.slice(from)
      if (segmentPoints.length >= 2) {
        this.paintStroke({ ...stroke, points: segmentPoints })
      }
      this.drawnStrokePoints.set(stroke.id, stroke.points.length)
      this.drawnStrokeIds.add(stroke.id)
    }
    for (const id of this.drawnStrokePoints.keys()) {
      if (!ids.has(id)) this.drawnStrokePoints.delete(id)
    }
  }

  private paintStroke(stroke: CrowdCanvasStroke) {
    if (!this.boardTex || stroke.points.length < 2) return
    strokeToContext(
      this.boardTex.getContext(),
      stroke.points,
      paintScale(),
      stroke.color,
      stroke.width,
    )
    this.boardTex.refresh()
  }

  private syncLabels(state: CrowdCanvasState) {
    if (state.phase === 'results' || state.phase === 'countdown') {
      for (const label of this.labels.values()) label.setVisible(false)
      return
    }
    const lastByPlayer = new Map<string, CrowdCanvasStroke>()
    for (const s of state.strokes) lastByPlayer.set(s.playerKey, s)
    const seen = new Set<string>()
    for (const p of state.players) {
      seen.add(p.playerKey)
      const stroke = lastByPlayer.get(p.playerKey)
      let label = this.labels.get(p.playerKey)
      if (!label) {
        label = this.add
          .text(0, 0, p.displayName, {
            fontFamily: 'Segoe UI, sans-serif',
            fontSize: '12px',
            color: '#e2e8f0',
            backgroundColor: '#0f172aaa',
            padding: { x: 4, y: 2 },
          })
          .setDepth(15)
          .setOrigin(0.5, 1)
        this.labels.set(p.playerKey, label)
      }
      label.setText(p.displayName)
      if (stroke?.points.length) {
        const tip = stroke.points[stroke.points.length - 1]
        label.setPosition(tip.x, tip.y - 6).setVisible(true)
      } else {
        label.setVisible(false)
      }
    }
    for (const [key, label] of this.labels) {
      if (!seen.has(key)) {
        label.destroy()
        this.labels.delete(key)
      }
    }
  }

  private syncHud(state: CrowdCanvasState) {
    const remainMs = Math.max(0, state.roundDurationMs - state.elapsedMs)
    const hudKey = `${state.phase}|${state.round}|${state.theme}|${Math.floor(remainMs / 1000)}|${state.finalStroke}|${state.artistCount}`
    if (hudKey === this.lastHudKey) return
    this.lastHudKey = hudKey

    const mm = String(Math.floor(remainMs / 60000)).padStart(2, '0')
    const ss = String(Math.floor((remainMs % 60000) / 1000)).padStart(2, '0')

    if (state.phase === 'lobby') {
      this.hudText?.setText(
        `Crowd Canvas · ${state.theme} · ${state.players.length} artists`,
      )
      this.banner?.setVisible(false)
    } else if (state.phase === 'countdown') {
      this.hudText?.setText(state.theme)
      this.banner?.setVisible(true)
    } else if (state.phase === 'playing') {
      this.hudText?.setText(
        state.finalStroke
          ? `FINAL STROKE · ${mm}:${ss} · ${state.artistCount} artists`
          : `Crowd Canvas · ${mm}:${ss} · ${state.artistCount} artists`,
      )
      this.banner?.setVisible(false)
    } else if (state.phase === 'results') {
      this.hudText?.setText('RESULT')
      this.banner?.setVisible(false)
    }
  }
}

export function createCrowdCanvasSpectator(parent: HTMLElement) {
  const scene = new CrowdCanvasSpectatorScene()
  let destroyed = false
  const dpr = deviceDpr()
  const game = new Phaser.Game({
    type: Phaser.AUTO,
    parent,
    width: Math.round(WORLD_W * dpr),
    height: Math.round(WORLD_H * dpr),
    backgroundColor: '#00000000',
    transparent: true,
    scene: [scene],
    scale: { mode: Phaser.Scale.FIT, autoCenter: Phaser.Scale.CENTER_BOTH },
    render: {
      antialias: true,
      roundPixels: false,
      powerPreference: 'high-performance',
    },
    fps: { target: 30 },
  })

  return {
    applyState(state: unknown) {
      if (destroyed || !isCrowdCanvasState(state)) return
      scene.applyState(state)
    },
    clear() {
      if (!destroyed) scene.clearBoard()
    },
    destroy() {
      destroyed = true
      game.destroy(true)
    },
  }
}
